import React, { useState } from "react";
import { getAuth } from "firebase/auth";
import {
  collection,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { Upload, Send, FileText } from "lucide-react";

import { db, storage } from "../web/src/firebaseClient";
import InnovationHubHeader from "../components/InnovationHubHeader";
import InnovationHubFooter from "../components/InnovationHubFooter";

export default function ResearchProjectApplicationPage() {
  const [form, setForm] = useState({
    applicantName: "",
    institution: "",
    title: "",
    email: "",
    phone: "",
    projectTitle: "",
    projectSummary: "",
    researchPurpose: "",
    participantGroup: "",
    gradeLevel: "",
    participantCount: "",
    dataCollectionMethod: "",
    startDate: "",
    endDate: "",
    audioVideoRecording: "",
    digitalToolUsage: "",
    digitalToolExplanation: "",
    schoolSupport: "",
    confirmation: false,
  });

  const [files, setFiles] = useState({
    projectFile: null,
    sampleFormFile: null,
  });

  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successId, setSuccessId] = useState("");

  const auth = getAuth();
  const user = auth.currentUser;

  const validateField = (name, value = form[name]) => {
    switch (name) {
      case "email": {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) return "E-posta zorunludur.";
        if (!emailRegex.test(value)) return "Geçerli bir e-posta adresi giriniz.";
        return "";
      }

      case "phone": {
        const phoneRegex = /^(\+90|0)?5\d{9}$/;
        const normalizedPhone = value.replace(/\s/g, "");
        if (!value.trim()) return "Telefon numarası zorunludur.";
        if (!phoneRegex.test(normalizedPhone)) {
          return "Telefon numarası 05xxxxxxxxx formatında olmalıdır.";
        }
        return "";
      }

      case "participantCount": {
        const count = Number(value);
        if (!value) return "Tahmini katılımcı sayısı zorunludur.";
        if (!Number.isInteger(count) || count < 1 || count > 1000) {
          return "Katılımcı sayısı 1 ile 1000 arasında tam sayı olmalıdır.";
        }
        return "";
      }

      case "startDate":
        if (!value) return "Başlangıç tarihi zorunludur.";
        return "";

      case "endDate":
        if (!value) return "Bitiş tarihi zorunludur.";
        if (form.startDate && value < form.startDate) {
          return "Bitiş tarihi başlangıç tarihinden önce olamaz.";
        }
        return "";

      case "projectSummary":
        if (!value.trim()) return "Proje özeti zorunludur.";
        if (value.trim().length > 1800) {
          return "Proje özeti en fazla 1800 karakter olabilir.";
        }
        return "";

      case "researchPurpose":
        if (!value.trim()) return "Araştırmanın amacı zorunludur.";
        if (value.trim().length > 1200) {
          return "Araştırmanın amacı en fazla 1200 karakter olabilir.";
        }
        return "";

      case "schoolSupport":
        if (!value.trim()) return "Okuldan beklenen destek zorunludur.";
        if (value.trim().length > 1000) {
          return "Okuldan beklenen destek en fazla 1000 karakter olabilir.";
        }
        return "";

      case "digitalToolExplanation":
        if (form.digitalToolUsage === "Evet" && !value.trim()) {
          return "Dijital araç / yapay zekâ kullanılacaksa kısa açıklama zorunludur.";
        }
        return "";

      default:
        if (typeof value === "string" && !value.trim()) {
          return "Bu alan zorunludur.";
        }
        return "";
    }
  };

  const validateForm = () => {
    const fieldsToValidate = [
      "applicantName",
      "institution",
      "title",
      "email",
      "phone",
      "projectTitle",
      "projectSummary",
      "researchPurpose",
      "participantGroup",
      "gradeLevel",
      "participantCount",
      "dataCollectionMethod",
      "startDate",
      "endDate",
      "audioVideoRecording",
      "digitalToolUsage",
      "schoolSupport",
    ];

    if (form.digitalToolUsage === "Evet") {
      fieldsToValidate.push("digitalToolExplanation");
    }

    const newErrors = {};

    fieldsToValidate.forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });

    if (!form.confirmation) {
      newErrors.confirmation = "Onay kutusu işaretlenmelidir.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid =
    form.applicantName &&
    form.institution &&
    form.title &&
    form.email &&
    form.phone &&
    form.projectTitle &&
    form.projectSummary &&
    form.researchPurpose &&
    form.participantGroup &&
    form.gradeLevel &&
    form.participantCount &&
    form.dataCollectionMethod &&
    form.startDate &&
    form.endDate &&
    form.audioVideoRecording &&
    form.digitalToolUsage &&
    form.schoolSupport &&
    form.confirmation &&
    (form.digitalToolUsage !== "Evet" || form.digitalToolExplanation);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;

    setForm((prev) => ({
      ...prev,
      [name]: nextValue,
    }));

    if (type !== "checkbox") {
      const error = validateField(name, nextValue);

      setErrors((prev) => ({
        ...prev,
        [name]: error,
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (name === "startDate" && form.endDate && form.endDate < nextValue) {
      setErrors((prev) => ({
        ...prev,
        endDate: "Bitiş tarihi başlangıç tarihinden önce olamaz.",
      }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^\d\s+]/g, "");

    setForm((prev) => ({
      ...prev,
      phone: value,
    }));

    const error = validateField("phone", value);

    setErrors((prev) => ({
      ...prev,
      phone: error,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles?.[0];

    if (!file) return;

    const validTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ];

    if (!validTypes.includes(file.type)) {
      setFileError("Sadece PDF, DOC veya DOCX formatında dosya yükleyebilirsin.");
      e.target.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setFileError("Dosya en fazla 10 MB olabilir.");
      e.target.value = "";
      return;
    }

    setFileError("");

    setFiles((prev) => ({
      ...prev,
      [name]: file,
    }));
  };

  const uploadOptionalFiles = async (docId) => {
    const uploadEntries = Object.entries(files).filter(([, file]) => file);

    if (uploadEntries.length === 0) return [];

    const uploadedFiles = await Promise.all(
      uploadEntries.map(async ([key, file]) => {
        const filePath = `researchProjectApplications/${user.uid}/${docId}/${key}-${Date.now()}-${file.name}`;
        const fileRef = ref(storage, filePath);

        await uploadBytes(fileRef, file, {
          contentType: file.type,
        });

        const url = await getDownloadURL(fileRef);

        return {
          key,
          name: file.name,
          type: file.type,
          size: file.size,
          path: filePath,
          url,
        };
      })
    );

    return uploadedFiles;
  };

  const resetForm = () => {
    setForm({
      applicantName: "",
      institution: "",
      title: "",
      email: "",
      phone: "",
      projectTitle: "",
      projectSummary: "",
      researchPurpose: "",
      participantGroup: "",
      gradeLevel: "",
      participantCount: "",
      dataCollectionMethod: "",
      startDate: "",
      endDate: "",
      audioVideoRecording: "",
      digitalToolUsage: "",
      digitalToolExplanation: "",
      schoolSupport: "",
      confirmation: false,
    });

    setFiles({
      projectFile: null,
      sampleFormFile: null,
    });

    setErrors({});
    setFileError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alert("Lütfen formdaki hataları düzeltin.");
      return;
    }

    if (!user) {
      alert("Başvuru göndermek için giriş yapmalısın.");
      return;
    }

    try {
      setLoading(true);

      const docRef = await addDoc(
        collection(db, "researchProjectApplications"),
        {
          ...form,
          plannedDates: {
            startDate: form.startDate,
            endDate: form.endDate,
          },
          uid: user.uid,
          uploadedFiles: [],
          status: "pending",
          isApproved: false,
          moderatedBy: "",
          moderatedAt: null,
          moderationNote: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }
      );

      const uploadedFiles = await uploadOptionalFiles(docRef.id);

      await updateDoc(docRef, {
        uploadedFiles,
        updatedAt: serverTimestamp(),
      });

      setSuccessId(docRef.id);
      resetForm();
    } catch (error) {
      console.error("Başvuru hatası:", error);
      alert(error.message || "Başvuru gönderilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-[#eef2f7] via-[#f4f7fb] to-[#e6ecf5]"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <InnovationHubHeader />

      <section className="px-4 py-6 md:px-6">
        <div className="mx-auto max-w-[1200px]">
          <div className="mb-6 rounded-[28px] border border-white/70 bg-white/80 p-6 shadow-[0_12px_30px_rgba(21,101,192,0.07)] backdrop-blur-sm">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-[3px] w-[60px] rounded-full bg-[#1565C0]" />
              <span className="h-[3px] w-[40px] rounded-full bg-[#C62828]" />
            </div>

            <h1 className="text-2xl font-semibold tracking-[-0.02em] text-[#173b7a] md:text-3xl">
              Araştırma Projesi Başvuru Formu
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
              Bu form, okul bünyesinde yürütülmesi planlanan araştırma
              projelerinin pedagojik, etik ve operasyonel açıdan
              değerlendirilmesi amacıyla kullanılmaktadır.
            </p>
          </div>

          {successId && (
            <div className="mb-6 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-700">
              Başvurun başarıyla alındı. Başvuru ID: {successId}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            noValidate
            className="rounded-[30px] bg-gradient-to-br from-[#1565C0]/20 via-white/60 to-[#C62828]/20 p-[1px]"
          >
            <div className="rounded-[29px] border border-white/70 bg-white/90 p-5 shadow-[0_12px_30px_rgba(21,101,192,0.08)] backdrop-blur-sm md:p-6">
              <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-6">
                  <FormSection title="Başvuru Sahibi Bilgileri">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input
                        label="Ad Soyad *"
                        name="applicantName"
                        value={form.applicantName}
                        onChange={handleChange}
                        error={errors.applicantName}
                        placeholder="Ad Soyad"
                      />

                      <Input
                        label="Kurum / Üniversite / Okul *"
                        name="institution"
                        value={form.institution}
                        onChange={handleChange}
                        error={errors.institution}
                        placeholder="Kurum adı"
                      />

                      <Input
                        label="Görev / Ünvan *"
                        name="title"
                        value={form.title}
                        onChange={handleChange}
                        error={errors.title}
                        placeholder="Örn. Öğretmen, Akademisyen"
                      />

                      <Input
                        label="E-posta *"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        error={errors.email}
                        placeholder="ornek@kurum.edu.tr"
                        inputMode="email"
                      />

                      <Input
                        label="Telefon *"
                        name="phone"
                        value={form.phone}
                        onChange={handlePhoneChange}
                        error={errors.phone}
                        placeholder="05xx xxx xx xx"
                        inputMode="tel"
                      />
                    </div>
                  </FormSection>

                  <FormSection title="Proje Bilgileri">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Input
                        label="Proje başlığı *"
                        name="projectTitle"
                        value={form.projectTitle}
                        onChange={handleChange}
                        error={errors.projectTitle}
                        placeholder="Örn. Yapay Zekâ ile Video Üretiminin Öğrenmeye Etkisi"
                      />

                      <Select
                        label="Hedef katılımcı grubu *"
                        name="participantGroup"
                        value={form.participantGroup}
                        onChange={handleChange}
                        error={errors.participantGroup}
                        options={[
                          "Öğrenciler",
                          "Öğretmenler",
                          "Veliler",
                          "Diğer",
                        ]}
                      />

                      <Input
                        label="Hedef yaş / sınıf düzeyi *"
                        name="gradeLevel"
                        value={form.gradeLevel}
                        onChange={handleChange}
                        error={errors.gradeLevel}
                        placeholder="Örn. 7. sınıf, lise hazırlık"
                      />

                      <Input
                        label="Tahmini katılımcı sayısı *"
                        name="participantCount"
                        type="number"
                        min="1"
                        max="1000"
                        value={form.participantCount}
                        onChange={handleChange}
                        error={errors.participantCount}
                        placeholder="Örn. 24"
                        inputMode="numeric"
                      />

                      <Select
                        label="Veri toplama yöntemi *"
                        name="dataCollectionMethod"
                        value={form.dataCollectionMethod}
                        onChange={handleChange}
                        error={errors.dataCollectionMethod}
                        options={[
                          "Anket",
                          "Görüşme",
                          "Gözlem",
                          "Akademik veri analizi",
                          "Dijital platform kullanımı",
                          "Öğrenci ürünleri",
                          "Diğer",
                        ]}
                      />

                      <Input
                        label="Başlangıç tarihi *"
                        name="startDate"
                        type="date"
                        value={form.startDate}
                        onChange={handleChange}
                        error={errors.startDate}
                      />

                      <Input
                        label="Bitiş tarihi *"
                        name="endDate"
                        type="date"
                        value={form.endDate}
                        onChange={handleChange}
                        error={errors.endDate}
                        min={form.startDate}
                      />
                    </div>

                    <Textarea
                      label="Projenin kısa özeti *"
                      name="projectSummary"
                      value={form.projectSummary}
                      onChange={handleChange}
                      error={errors.projectSummary}
                      maxLength={1800}
                      placeholder="Araştırmayı kısa ve anlaşılır şekilde özetleyin. Maksimum 300 kelime önerilir."
                    />

                    <Textarea
                      label="Araştırmanın amacı *"
                      name="researchPurpose"
                      value={form.researchPurpose}
                      onChange={handleChange}
                      error={errors.researchPurpose}
                      maxLength={1200}
                      placeholder="Bu araştırma ile neyi incelemek, anlamak veya ölçmek istiyorsunuz?"
                    />
                  </FormSection>

                  <FormSection title="Etik ve Güvenlik">
                    <div className="grid gap-4 md:grid-cols-2">
                      <Select
                        label="Ses / video kaydı alınacak mı? *"
                        name="audioVideoRecording"
                        value={form.audioVideoRecording}
                        onChange={handleChange}
                        error={errors.audioVideoRecording}
                        options={["Evet", "Hayır"]}
                      />

                      <Select
                        label="Dijital araç veya yapay zekâ sistemi kullanılacak mı? *"
                        name="digitalToolUsage"
                        value={form.digitalToolUsage}
                        onChange={handleChange}
                        error={errors.digitalToolUsage}
                        options={["Evet", "Hayır"]}
                      />
                    </div>

                    {form.digitalToolUsage === "Evet" && (
                      <Textarea
                        label="Kullanılacak dijital araç / yapay zekâ sistemi *"
                        name="digitalToolExplanation"
                        value={form.digitalToolExplanation}
                        onChange={handleChange}
                        error={errors.digitalToolExplanation}
                        placeholder="Örn. Canva AI, Adobe Express, ChatGPT, video üretim aracı vb."
                      />
                    )}
                  </FormSection>

                  <FormSection title="Okuldan Beklenen Destek">
                    <Textarea
                      label="Okuldan beklenen destek *"
                      name="schoolSupport"
                      value={form.schoolSupport}
                      onChange={handleChange}
                      error={errors.schoolSupport}
                      maxLength={1000}
                      placeholder="Örn. sınıf kullanımı, öğretmen iş birliği, teknik destek, öğrenci grubuna erişim vb."
                    />
                  </FormSection>
                </div>

                <div className="space-y-5">
                  <div className="rounded-2xl bg-[#f8fbff] p-4">
                    <h3 className="text-sm font-semibold text-[#173b7a]">
                      Başvuru notu
                    </h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Başvurular; öğrenci güvenliği, veri gizliliği ve okul
                      ortamında uygulanabilirlik açısından değerlendirilecektir.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-white p-4">
                    <h3 className="mb-4 text-sm font-semibold text-[#173b7a]">
                      Dosya Yükleme
                    </h3>

                    <div className="space-y-3">
                      <FileUpload
                        label="Proje dosyası / araştırma önerisi"
                        name="projectFile"
                        file={files.projectFile}
                        onChange={handleFileChange}
                      />

                      <FileUpload
                        label="Örnek anket / görüşme formu"
                        name="sampleFormFile"
                        file={files.sampleFormFile}
                        onChange={handleFileChange}
                      />
                    </div>

                    {fileError && (
                      <p className="mt-3 rounded-xl bg-[#C62828]/10 px-3 py-2 text-xs font-medium text-[#C62828]">
                        {fileError}
                      </p>
                    )}

                    <p className="mt-3 text-xs leading-5 text-slate-500">
                      • Dosya yüklemek opsiyoneldir.
                      <br />• PDF, DOC ve DOCX formatları kabul edilir.
                      <br />• Her dosya en fazla 10 MB olabilir.
                    </p>
                  </div>

                  <label className="flex gap-3 rounded-2xl border border-[#1565C0]/10 bg-[#1565C0]/5 p-4">
                    <input
                      type="checkbox"
                      name="confirmation"
                      checked={form.confirmation}
                      onChange={handleChange}
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-[#1565C0] focus:ring-[#1565C0]"
                    />

                    <span className="text-sm leading-6 text-slate-600">
                      Başvuruda paylaşılan bilgilerin doğru olduğunu ve araştırma
                      sürecinde öğrenci güvenliği, etik ilkeler ve veri gizliliği
                      kurallarına uygun hareket edeceğimi kabul ediyorum.
                    </span>
                  </label>

                  {errors.confirmation && (
                    <p className="rounded-xl bg-[#C62828]/10 px-3 py-2 text-xs font-medium text-[#C62828]">
                      {errors.confirmation}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={!isFormValid || loading}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#1565C0] px-5 py-4 text-sm font-semibold text-white transition hover:bg-[#0f57aa] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Send size={17} />
                    {loading ? "Gönderiliyor..." : "Başvuruyu Gönder"}
                  </button>

                  {!isFormValid && (
                    <p className="text-xs leading-5 text-slate-500">
                      Başvuruyu göndermek için zorunlu alanları doğru formatta
                      doldurmalı ve onay kutusunu işaretlemelisin.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      <InnovationHubFooter />
    </main>
  );
}

function FormSection({ title, children }) {
  return (
    <section className="rounded-2xl border border-gray-100 bg-white/80 p-4 md:p-5">
      <h2 className="mb-4 text-base font-semibold text-[#173b7a]">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Input({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  error,
  inputMode,
  min,
  max,
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[#173b7a]">
        {label}
      </span>

      <input
        required
        type={type}
        name={name}
        value={value}
        min={min}
        max={max}
        inputMode={inputMode}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 ${
          error
            ? "border-[#C62828] focus:border-[#C62828] focus:ring-[#C62828]/10"
            : "border-gray-200 focus:border-[#1565C0] focus:ring-[#1565C0]/10"
        }`}
      />

      {error && (
        <p className="mt-2 rounded-lg bg-[#C62828]/10 px-3 py-2 text-xs font-medium text-[#C62828]">
          {error}
        </p>
      )}
    </label>
  );
}

function Select({ label, name, value, onChange, options, error }) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[#173b7a]">
        {label}
      </span>

      <select
        required
        name={name}
        value={value}
        onChange={onChange}
        className={`w-full rounded-xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 ${
          error
            ? "border-[#C62828] focus:border-[#C62828] focus:ring-[#C62828]/10"
            : "border-gray-200 focus:border-[#1565C0] focus:ring-[#1565C0]/10"
        }`}
      >
        <option value="">Seçiniz</option>
        {options.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>

      {error && (
        <p className="mt-2 rounded-lg bg-[#C62828]/10 px-3 py-2 text-xs font-medium text-[#C62828]">
          {error}
        </p>
      )}
    </label>
  );
}

function Textarea({
  label,
  name,
  value,
  onChange,
  placeholder,
  rows = 4,
  error,
  maxLength,
}) {
  return (
    <label className="block">
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="block text-sm font-medium text-[#173b7a]">
          {label}
        </span>

        {maxLength && (
          <span className="text-xs text-slate-400">
            {value.length}/{maxLength}
          </span>
        )}
      </div>

      <textarea
        required
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        className={`w-full resize-none rounded-xl border bg-white px-4 py-3 text-sm outline-none transition focus:ring-4 ${
          error
            ? "border-[#C62828] focus:border-[#C62828] focus:ring-[#C62828]/10"
            : "border-gray-200 focus:border-[#1565C0] focus:ring-[#1565C0]/10"
        }`}
      />

      {error && (
        <p className="mt-2 rounded-lg bg-[#C62828]/10 px-3 py-2 text-xs font-medium text-[#C62828]">
          {error}
        </p>
      )}
    </label>
  );
}

function FileUpload({ label, name, file, onChange }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-[#1565C0]/25 bg-[#f8fbff] p-4 transition hover:bg-[#eef6ff]">
      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#1565C0]/10 text-[#1565C0]">
        {file ? <FileText size={18} /> : <Upload size={18} />}
      </div>

      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-[#173b7a]">{label}</div>
        <div className="mt-1 truncate text-xs text-slate-500">
          {file ? file.name : "PDF, DOC veya DOCX yükle"}
        </div>
      </div>

      <input
        type="file"
        name={name}
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={onChange}
        className="hidden"
      />
    </label>
  );
}